import React, { useState, useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Pencil, X, Info } from "lucide-react";
import Modal from "components/common/Modal";
import Combobox from "v2/views/components/common/Combobox";
import DatePicker from "components/common/DatePicker";
import {
  getPositionsForSport,
  positionLabel,
  positionToApi,
} from "v2/constants/positions";

const EditPlayerModal = ({ show, onClose, onSave, player, teamSport }) => {
  const { t } = useTranslation();
  const positionSuggestions = useMemo(
    () => getPositionsForSport(teamSport).map((p) => p.label),
    [teamSport]
  );
  const [playerName, setPlayerName] = useState("");
  const [position, setPosition] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState(null);

  useEffect(() => {
    if (show && player) {
      setPlayerName(player.name || "");
      setPosition(positionLabel(player.position) || "");
      // Parse date of birth if it exists
      setDateOfBirth(player.dateOfBirth ? new Date(player.dateOfBirth) : null);
    }
  }, [show, player]);

  const handleClose = () => {
    setPlayerName(player?.name || "");
    setPosition(positionLabel(player?.position) || "");
    setDateOfBirth(player?.dateOfBirth ? new Date(player.dateOfBirth) : null);
    onClose();
  };

  const handleSave = () => {
    if (!playerName.trim()) return;
    onSave?.({
      name: playerName.trim(),
      position: positionToApi(position.trim(), teamSport) || "",
      date_of_birth: dateOfBirth
        ? dateOfBirth.toISOString().split("T")[0]
        : null,
    });
    handleClose();
  };

  const isValid = playerName.trim().length > 0;
  const hasChanged =
    playerName.trim() !== (player?.name || "").trim() ||
    positionToApi(position.trim(), teamSport).toLowerCase() !==
      (player?.position || "").toLowerCase() ||
    (dateOfBirth?.toISOString().split("T")[0] || null) !==
      (player?.dateOfBirth || null);

  return (
    <Modal show={show} onClose={handleClose} maxWidth="480px">
      {/* Header */}
      <div
        className="relative overflow-hidden p-7"
        style={{
          background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
        }}
      >
        {/* Decorative circles */}
        <div
          className="absolute rounded-full"
          style={{
            width: 120,
            height: 120,
            top: -30,
            right: -30,
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 80,
            height: 80,
            bottom: -40,
            right: 60,
            background: "rgba(255, 255, 255, 0.08)",
          }}
        />

        {/* Header content */}
        <div className="relative flex items-start justify-between">
          <div>
            {/* Icon */}
            <div
              className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <Pencil size={24} className="text-white" />
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-white">
              {t("workspace.editPlayerModal.title")}
            </h2>
            <p
              className="mt-1.5 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              {t("workspace.editPlayerModal.subtitle")}
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150"
            style={{ background: "rgba(255, 255, 255, 0.2)" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")
            }
          >
            <X size={18} className="text-white" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-7">
        {/* Player Name Field */}
        <div className="mb-5">
          <label className="mb-2 block text-sm font-medium text-[#374151]">
            {t("workspace.addPlayerModal.nameLabel")}
          </label>
          <input
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder={t("workspace.addPlayerModal.namePlaceholder")}
            className="w-full rounded-[10px] border border-[#E5E7EB] px-4 py-3 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150"
            style={{
              boxShadow: "none",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#E07A3A";
              e.target.style.boxShadow = "0 0 0 3px rgba(224, 122, 58, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#E5E7EB";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Date of Birth Field */}
        <div className="mb-5">
          <label className="mb-2 block text-sm font-medium text-[#374151]">
            <Trans
              i18nKey="workspace.addPlayerModal.dobLabel"
              components={{
                muted: <span className="font-normal text-[#9CA3AF]" />,
              }}
            />
          </label>
          <DatePicker
            value={dateOfBirth}
            onChange={setDateOfBirth}
            placeholder={t("workspace.addPlayerModal.dobPlaceholder")}
            maxDate={new Date()}
          />
        </div>

        {/* Position Selector */}
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-[#374151]">
            <Trans
              i18nKey="workspace.addPlayerModal.positionLabel"
              components={{
                muted: <span className="font-normal text-[#9CA3AF]" />,
              }}
            />
          </label>
          <Combobox
            suggestions={positionSuggestions}
            value={position}
            onChange={setPosition}
            placeholder={t("workspace.addPlayerModal.positionPlaceholder")}
          />
        </div>

        {/* Info Box */}
        <div
          className="flex gap-3 rounded-xl p-4"
          style={{
            background:
              "linear-gradient(135deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 100%)",
            border: "1px solid rgba(232, 133, 74, 0.15)",
          }}
        >
          <Info size={20} className="mt-0.5 flex-shrink-0 text-[#E07A3A]" />
          <p className="text-[13px] leading-relaxed text-[#6B7280]">
            {t("workspace.editPlayerModal.info")}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 border-t border-[#F3F4F6] px-7 py-5">
        <button
          onClick={handleClose}
          className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
        >
          {t("common.actions.cancel")}
        </button>
        <button
          onClick={handleSave}
          disabled={!isValid || !hasChanged}
          className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
          style={
            isValid && hasChanged
              ? {
                  background:
                    "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                  boxShadow: "0 2px 8px rgba(224, 122, 58, 0.3)",
                  cursor: "pointer",
                }
              : {
                  background: "#F3F4F6",
                  color: "#9CA3AF",
                  cursor: "not-allowed",
                }
          }
        >
          {t("workspace.shared.saveChanges")}
        </button>
      </div>
    </Modal>
  );
};

export default EditPlayerModal;
